import React from "react";
import { Box, Container, Grid, Paper, Stack, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import aboutImage from "../assets/images/aboutUs.jpg";
import novaImage from "../assets/images/nova-en-nicoline.jpeg";

const StyledAboutSection = styled(Box)(({ theme }) => ({
  padding: theme.spacing(9, 0),
  backgroundColor: theme.palette.background.default,
}));

const Lead = styled(Typography)(({ theme }) => ({
  width: "100%",
  maxWidth: 720,
  margin: "0 auto",
  textAlign: "center",
  color: theme.palette.text.secondary,
  lineHeight: 1.75,
  fontSize: "1.05rem",
  [theme.breakpoints.down("sm")]: {
    maxWidth: "100%",
    padding: theme.spacing(0, 1),
  },
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  color: theme.palette.primary.main,
  marginBottom: theme.spacing(2),
}));

const StoryText = styled(Typography)(({ theme }) => ({
  lineHeight: 1.82,
  marginBottom: theme.spacing(2),
  "& strong": {
    fontWeight: 600,
  },
}));

const StoryImage = styled("img")<{ $portrait?: boolean }>(({ theme, $portrait }) => ({
  width: "100%",
  height: $portrait ? 520 : 430,
  objectFit: "cover",
  objectPosition: $portrait ? "center 35%" : "center",
  borderRadius: theme.spacing(2),
  boxShadow: theme.shadows[3],
  [theme.breakpoints.down("md")]: {
    height: $portrait ? 430 : 360,
  },
}));

const SoftCard = styled(Paper)<{ $tone?: "green" | "pink" }>(({ theme, $tone = "green" }) => ({
  height: "100%",
  padding: theme.spacing(4),
  borderRadius: theme.spacing(2),
  backgroundColor:
    theme.palette.mode === "dark"
      ? theme.palette.background.paper
      : $tone === "pink"
        ? theme.palette.primary.light
        : theme.palette.secondary.light,
  boxShadow: "none",
}));

const PullQuote = styled(Typography)(({ theme }) => ({
  fontSize: "1.12rem",
  lineHeight: 1.65,
  fontWeight: 600,
  color: theme.palette.text.primary,
}));

export const AboutSection: React.FC = () => {
  return (
    <StyledAboutSection id="about" component="section" aria-labelledby="about-heading">
      <Container maxWidth="lg">
        <Stack
          spacing={1.5}
          alignItems="center"
          sx={{ mb: { xs: 5, md: 7 }, textAlign: "center" }}
        >
          <SectionTitle
            variant="h3"
            component="h2"
            id="about-heading"
            sx={{ mb: 0, width: "100%", textAlign: "center" }}
          >
            Over Pippaya
          </SectionTitle>
          <Lead>
            Geen kennels en geen grote groepen, maar een kleinschalige opvang waar
            een hond echt mee kan draaien in het gewone leven. Een beetje alsof er
            tijdelijk een extra hond bij ons woont.
          </Lead>
        </Stack>

        <Grid
          container
          spacing={{ xs: 4, md: 6 }}
          alignItems="center"
          sx={{ mb: { xs: 7, md: 9 } }}
        >
          <Grid item xs={12} md={6}>
            <SectionTitle variant="h4" component="h3">
              Een huis waar honden gewoon meedoen
            </SectionTitle>
            <StoryText>
              Pippaya is ontstaan na het verlies van onze twee meiden. Ineens was
              het stil in huis. Geen hondenpoten meer om ons heen, geen haren
              overal, geen natte neuzen en ook niemand meer die precies weet
              wanneer er ergens een zakje met iets lekkers opengaat. 😉
            </StoryText>
            <StoryText>
              Uit dat gemis ontstond langzaam het idee voor Pippaya. Maar ik wilde
              geen groot hondenpension beginnen. Geen rijen kennels en geen massale
              opvang. Ik wilde juist een plek waar een hond lekker mee kan draaien
              in het gewone leven en zich echt thuis kan voelen.
            </StoryText>
            <PullQuote>Hier mag een hond vooral gewoon hond zijn. 💗</PullQuote>
          </Grid>

          <Grid item xs={12} md={6}>
            <StoryImage
              src={aboutImage}
              alt="Pippaya Hondenopvang in de dagelijkse praktijk"
            />
          </Grid>
        </Grid>

        <SoftCard $tone="green" sx={{ mb: { xs: 7, md: 9 } }}>
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <SectionTitle variant="h4" component="h3">
                Wandelen, spelen, snuffelen… en uitrusten
              </SectionTitle>
              <StoryText>
                We gaan wandelen, trekken lekker de polder in en als het kan gaan
                we zwemmen. De ene keer komen we redelijk toonbaar thuis en de
                andere keer heb ik een stel verzopen moddermonsters bij me. 😂
                Maar ach, daar hebben ze handdoeken en een wasmachine voor
                uitgevonden.
              </StoryText>
              <StoryText sx={{ mb: 0 }}>
                Er wordt gespeeld, gesnuffeld en geknuffeld, maar het hoeft hier
                echt niet de hele dag feest te zijn. Na een flinke wandeling of
                zwempartij is ergens neerploffen en een paar uur helemaal niets
                doen minstens zo belangrijk.
              </StoryText>
            </Grid>

            <Grid item xs={12} md={6}>
              <SectionTitle variant="h4" component="h3">
                Iedere hond is anders
              </SectionTitle>
              <StoryText>
                De één voelt zich binnen vijf minuten volledig thuis en de ander
                heeft even tijd nodig om alles rustig te bekijken. De één ligt het
                liefst tegen je aan, de ander zoekt liever zijn eigen fijne plekje.
                En waar de ene hond dolgelukkig van water wordt, denkt de ander:
                {" "}
                <strong>zoek het lekker uit, ik blijf hier wel staan.</strong> 😂
              </StoryText>
              <StoryText sx={{ mb: 0 }}>
                Daarom houd ik Pippaya bewust klein. Ik wil de honden die hier
                komen echt leren kennen: weten waar ze blij van worden, wanneer
                ze rust nodig hebben en welke kleine eigenaardigheden bij ze horen.
              </StoryText>
            </Grid>
          </Grid>
        </SoftCard>

        <Grid
          container
          spacing={{ xs: 4, md: 6 }}
          alignItems="center"
          sx={{ mb: { xs: 7, md: 9 } }}
        >
          <Grid item xs={12} md={5}>
            <StoryImage $portrait src={novaImage} alt="Nicoline met Nova" />
          </Grid>

          <Grid item xs={12} md={7}>
            <SectionTitle variant="h4" component="h3">
              En toen kwam Nova 💜
            </SectionTitle>
            <StoryText>
              Sinds <strong>1 augustus 2026</strong> hebben Mark en ik zelf weer een
              hond in huis: onze Flatcoated Retriever <strong>Nova</strong>. En
              daarmee begon er voor ons natuurlijk ook weer een heel nieuw avontuur.
            </StoryText>
            <StoryText>
              Nova groeit op tussen de logeerhonden van Pippaya. Voor haar is het
              heel normaal dat er regelmatig andere honden bij ons thuis zijn. De
              ene keer is er een heerlijke speelkameraad, de andere keer een wat
              oudere hond die helemaal geen behoefte heeft aan enthousiast
              puppygedoe. Ook dát moet je als jonge dame leren. 😉
            </StoryText>
            <StoryText>
              Nova gaat mee wandelen, ontdekt het zwemmen en is samen met mij volop
              aan het leren. We trainen samen en doen jachttraining. Ze woont hier
              nog maar sinds 1 augustus, maar Pippaya zonder Nova kunnen we ons
              eigenlijk nu al niet meer voorstellen.
            </StoryText>
          </Grid>
        </Grid>

        <Grid container spacing={3} sx={{ mb: 5 }} alignItems="stretch">
          <Grid item xs={12} md={6}>
            <SoftCard $tone="pink">
              <SectionTitle
                variant="h4"
                component="h3"
                sx={{ color: "text.primary" }}
              >
                Liefde voor honden is de basis
              </SectionTitle>
              <StoryText>
                Pippaya is begonnen vanuit mijn liefde voor honden, maar ik vind het
                ook belangrijk om te weten wat ik doe. Daarom heb ik mijn certificaat
                {" "}
                Vakbekwaamheid honden en katten behaald.
              </StoryText>
              <StoryText sx={{ mb: 0 }}>
                Mensen vertrouwen mij hun hond toe en daar ga ik serieus mee om. Ik
                let erop of een hond lekker in z&apos;n vel zit, zorg voor voldoende
                rust en kijk goed welke honden bij elkaar passen. Niet iedere hond
                hoeft met iedere andere hond dikke vrienden te worden. Als iedereen
                zich hier maar veilig en prettig voelt.
              </StoryText>
            </SoftCard>
          </Grid>

          <Grid item xs={12} md={6}>
            <SoftCard $tone="green">
              <SectionTitle
                variant="h4"
                component="h3"
                sx={{ color: "text.primary" }}
              >
                Gewoon Pippaya
              </SectionTitle>
              <StoryText>
                Soms is het hier een gezellige bende: rennen door de tuin, speelgoed
                overal en natte poten over de vloer. En soms ligt de hele club voor
                pampus en hoor je helemaal niets. Ik vind het allebei heerlijk.
              </StoryText>
              <StoryText sx={{ fontWeight: 500 }}>
                Geen kennel. Geen massale opvang. Geen hondenhotel.
              </StoryText>
              <StoryText sx={{ mb: 0 }}>
                Wel wandelen, zwemmen, spelen, snuffelen, modderpoten, lekkere
                snackjes, knuffels, fijne slaapplekjes en vooral veel aandacht voor
                iedere hond die hier komt logeren.
              </StoryText>
            </SoftCard>
          </Grid>
        </Grid>

        <Box sx={{ textAlign: "center", maxWidth: 820, mx: "auto", pt: 2 }}>
          <Typography
            variant="h6"
            component="p"
            sx={{
              fontWeight: 500,
              color: "primary.main",
              lineHeight: 1.6,
              textAlign: "center",
              maxWidth: 760,
              mx: "auto",
            }}
          >
            Welkom bij Pippaya — waar de baasjes op vakantie gaan… en de honden
            stiekem misschien nog nét iets meer. 💗🐾
          </Typography>
        </Box>
      </Container>
    </StyledAboutSection>
  );
};
